import React, { useCallback, useEffect } from 'react';
import {
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
  Alert,
} from 'react-native';
import { Text, useThemeName } from '@tamagui/core';
import { YStack, XStack } from '@tamagui/stacks';
import { Calendar, MapPin, Users, Star, BadgeCheck } from 'lucide-react-native';
import { Screen, ScreenHeader, AuthenticatedImage } from '@/components';
import { COLORS } from '@/constants/colors';
import { useTranslation } from 'react-i18next';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { RootStackParamList } from '@/types/navigation';
import {
  useAnnouncementApplications,
  useConfirmMeetingCompletion,
} from '@/api/hooks/useMeetingApplications';
import { useCreateChat, useAnnouncementChats, meetingChatKeys } from '@/api/hooks/useMeetingChats';
import { usePublicProfile, useMyProfile } from '@/api/hooks/useProfile';
import { chatService, ChatParticipant } from '@/services/firebase';
import { useQueryClient } from '@tanstack/react-query';
import type { MeetingAnnouncementApplicationView } from '@/api/types';
import { useApplicationNotifications } from '@/hooks/useApplicationNotifications';

type RouteParams = RouteProp<RootStackParamList, 'AnnouncementApplications'>;
type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

// Application Card Component
type ApplicationCardProps = {
  application: MeetingAnnouncementApplicationView;
  cardBg: string;
  textColor: string;
  subtitleColor: string;
  borderColor: string;
  isNew?: boolean;
  onViewProfile: () => void;
  onStartChat: (applicantProfile: any) => void;
  isStartingChat: boolean;
};

const ApplicationCard: React.FC<ApplicationCardProps> = ({
  application,
  cardBg,
  textColor,
  subtitleColor,
  borderColor,
  isNew = false,
  onViewProfile,
  onStartChat,
  isStartingChat,
}) => {
  const { t } = useTranslation();

  // Fetch applicant's public profile for photo and rating
  const { data: applicantProfile } = usePublicProfile(application.applicantId);

  const displayName = application.applicantNickname || 'Anonim';
  const isSelected =
    application.statusCode === 'accepted' || application.hostConfirmed;

  // Get default photo from profile
  const photoUrl =
    applicantProfile?.photos?.find(p => p.isDefault)?.thumbnailUrl ||
    applicantProfile?.photos?.[0]?.thumbnailUrl;

  // Format time ago
  const formatTimeAgo = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays} ${t('screens.announcementApplications.daysAgo')}`;
    } else if (diffHours > 0) {
      return `${diffHours} ${t('screens.announcementApplications.hoursAgo')}`;
    } else {
      return t('screens.announcementApplications.justNow');
    }
  };

  // Get age from profile
  const age = applicantProfile?.age;

  // Get verification status
  const isVerified = applicantProfile?.isVerified;

  return (
    <YStack
      backgroundColor={cardBg}
      borderRadius={16}
      padding="$4"
      borderWidth={1}
      borderColor={borderColor}
      gap="$3"
    >
      {/* Header: Photo, Name, Badge, Time */}
      <XStack gap="$3" alignItems="flex-start">
        {/* Photo */}
        <YStack
          width={50}
          height={50}
          borderRadius={25}
          overflow="hidden"
          backgroundColor={subtitleColor}
        >
          {photoUrl ? (
            <AuthenticatedImage
              uri={photoUrl}
              style={{ width: 50, height: 50 }}
            />
          ) : (
            <YStack flex={1} alignItems="center" justifyContent="center">
              <Text fontSize={20} color="#fff">
                {displayName.charAt(0).toUpperCase()}
              </Text>
            </YStack>
          )}
        </YStack>

        {/* Name and Rating */}
        <YStack flex={1}>
          <XStack alignItems="center" gap="$1">
            <Text
              fontSize={16}
              fontWeight="600"
              fontFamily="Poppins"
              color={textColor}
            >
              {displayName}
            </Text>
            {isVerified && <BadgeCheck size={16} color="#22c55e" />}

            {/* New Badge */}
            {isNew && (
              <XStack
                backgroundColor={COLORS.PRIMARY}
                paddingHorizontal="$2"
                paddingVertical="$1"
                borderRadius={6}
                marginLeft="$2"
              >
                <Text fontSize={12} fontWeight="600" color="white">
                  {t('screens.announcementApplications.new', 'nowe')}
                </Text>
              </XStack>
            )}

            {/* Selected Badge */}
            {isSelected && (
              <XStack
                backgroundColor="rgba(34, 197, 94, 0.15)"
                paddingHorizontal="$2"
                paddingVertical="$1"
                borderRadius={6}
                marginLeft="$2"
              >
                <Text fontSize={12} fontWeight="600" color="#22c55e">
                  {t('screens.announcementApplications.selected')}
                </Text>
              </XStack>
            )}
          </XStack>

          {age && (
            <Text fontSize={12} fontFamily="Poppins" color={subtitleColor}>
              {age} {t('screens.announcementApplications.years')}
            </Text>
          )}

          <XStack alignItems="center" gap="$1">
            <Star size={12} color="#fbbf24" fill="#fbbf24" />
            <Text fontSize={12} fontFamily="Poppins" color={subtitleColor}>
              {applicantProfile && applicantProfile.ratingAverage > 0
                ? `${applicantProfile.ratingAverage.toFixed(1)} (${
                    applicantProfile.ratingCount
                  } ${t('screens.announcementApplications.reviews')})`
                : t('screens.announcementApplications.noRating')}
            </Text>
          </XStack>
        </YStack>

        {/* Time ago */}
        <Text fontSize={12} fontFamily="Poppins" color={subtitleColor}>
          {formatTimeAgo(application.createdAt)}
        </Text>
      </XStack>

      {/* Motivation text */}
      {application.motivation && (
        <Text
          fontSize={14}
          fontFamily="Poppins"
          color={textColor}
          lineHeight={20}
        >
          {application.motivation}
        </Text>
      )}

      {/* Proposed amount if different */}
      {application.proposedAmount &&
        parseFloat(application.proposedAmount) > 0 && (
          <XStack alignItems="center" gap="$1">
            <Text fontSize={12} fontFamily="Poppins" color={subtitleColor}>
              {t('screens.announcementApplications.proposedAmount')}:
            </Text>
            <Text
              fontSize={12}
              fontWeight="600"
              fontFamily="Poppins"
              color={textColor}
            >
              {application.proposedAmount} zł
            </Text>
          </XStack>
        )}

      {/* Action Buttons */}
      <XStack gap="$3">
        <TouchableOpacity style={{ flex: 1 }} onPress={onViewProfile}>
          <YStack
            backgroundColor="transparent"
            borderRadius={12}
            paddingVertical="$2.5"
            alignItems="center"
            borderWidth={1}
            borderColor={COLORS.PRIMARY}
          >
            <Text fontSize={14} fontFamily="Poppins" color={COLORS.PRIMARY}>
              {t('screens.announcementApplications.viewProfile')}
            </Text>
          </YStack>
        </TouchableOpacity>

        <TouchableOpacity
          style={{ flex: 1 }}
          onPress={() => onStartChat(applicantProfile)}
          disabled={isStartingChat}
        >
          <YStack
            backgroundColor={COLORS.PRIMARY}
            borderRadius={12}
            paddingVertical="$2.5"
            alignItems="center"
          >
            {isStartingChat ? (
              <ActivityIndicator size="small" color="white" />
            ) : (
              <Text fontSize={14} fontFamily="Poppins" color="white">
                {t('screens.announcementApplications.startChat')}
              </Text>
            )}
          </YStack>
        </TouchableOpacity>
      </XStack>
    </YStack>
  );
};

// Main Screen Component
const AnnouncementApplicationsScreen: React.FC = () => {
  const themeName = useThemeName();
  const { t } = useTranslation();
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteParams>();
  const { announcementId, announcement } = route.params;
  const [startingChatForId, setStartingChatForId] = React.useState<string | null>(null);

  const isDark = themeName === 'dark';
  const bgColor = isDark ? COLORS.BLACK : '#f5f5f5';
  const cardBg = isDark ? '#1a1a1a' : COLORS.WHITE;
  const textColor = isDark ? '#ffffff' : '#18181b';
  const subtitleColor = isDark ? '#a1a1aa' : '#71717a';
  const borderColor = isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)';

  // Fetch current user's profile
  const { data: myProfile } = useMyProfile();
  
  // Get application notifications functions
  const { isApplicationNew, markAllForAnnouncementAsSeen } = useApplicationNotifications();

  // Fetch applications for this announcement
  const {
    data: applications = [],
    isLoading,
    isError,
    refetch,
    isRefetching,
  } = useAnnouncementApplications(announcementId);

  // Fetch existing chats for this announcement
  const { data: existingChats = [] } = useAnnouncementChats(announcementId);

  // Confirm application mutation
  const { mutateAsync: confirmApplication } = useConfirmMeetingCompletion();

  // Create chat in API
  const { mutateAsync: createChatInApi } = useCreateChat();

  // Query client for manual refetch
  const queryClient = useQueryClient();

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  // Mark all applications as seen when entering the screen
  useEffect(() => {
    if (announcementId && applications.length > 0) {
      markAllForAnnouncementAsSeen(announcementId);
    }
  }, [announcementId, applications.length, markAllForAnnouncementAsSeen]);

  const handleViewProfile = useCallback(
    (applicantId: string) => {
      navigation.navigate('UserProfile', { userId: applicantId });
    },
    [navigation],
  );

  const handleStartChat = useCallback(
    async (application: MeetingAnnouncementApplicationView, applicantProfile: any) => {
      if (!myProfile?.id) {
        Alert.alert(t('common.error'), 'Musisz być zalogowany');
        return;
      }
      
      const userId = myProfile.id;

      const isAlreadyAccepted = application.statusCode === 'accepted' || application.hostConfirmed;

      setStartingChatForId(application.id);

      try {
        const participantData: ChatParticipant = {
          odataId: application.applicantId,
          odataName: application.applicantNickname || 'Anonim',
          odataAvatar: applicantProfile?.photos?.find((p: any) => p.isDefault)?.thumbnailUrl || applicantProfile?.photos?.[0]?.thumbnailUrl,
          odataVerified: applicantProfile?.isVerified,
        };

        const meetingInfo = announcement
          ? {
              date: announcement.meetingDate,
              time: announcement.meetingTime,
              location: announcement.meetingAddress,
              announcementId: announcementId,
              amount: announcement.amount ?? 0,
            }
          : undefined;

        // Jeśli już zaakceptowana, znajdź istniejący czat z API
        if (isAlreadyAccepted && existingChats && existingChats.length > 0) {
          const existingChat = existingChats.find(
            chat => chat.meetingAnnouncementApplicationId === application.id
          );

          if (existingChat) {
            // Nawiguj do istniejącego czatu
            navigation.navigate('TabNavigator', {
              screen: 'Chat',
              params: {
                initialChatId: existingChat.firebaseChatId,
                initialChatName: participantData.odataName,
                initialChatImageUri: participantData.odataAvatar || '',
                initialChatMeetingInfo: meetingInfo,
              },
            });
            return;
          }
        }

        // Jeśli nie jest jeszcze zaakceptowana, potwierdź aplikację
        if (!isAlreadyAccepted) {
          await confirmApplication({
            applicationId: application.id,
            data: { confirm: true },
          });
        }

        // Przygotuj dane uczestników dla Firebase
        // Zamień /users/me/ na /users/{userId}/ w URL avatara, żeby link działał dla innych użytkowników
        const myAvatarUrl = myProfile.photos?.find((p: any) => p.isDefault)?.thumbnailUrl || myProfile.photos?.[0]?.thumbnailUrl;
        const fixedAvatarUrl = myAvatarUrl?.replace('/users/me/', `/users/${userId}/`);
        
        const currentUserData: ChatParticipant = {
          odataId: userId,
          odataName: myProfile.nickName || 'Anonim',
          odataAvatar: fixedAvatarUrl,
          odataVerified: myProfile.isVerified,
        };

        // Znajdź istniejący czat lub utwórz nowy w Firebase
        const firebaseChatId = await chatService.getOrCreateChat(
          userId,
          currentUserData,
          {
            participantId: application.applicantId,
            participantData,
            meetingInfo,
          }
        );

        if (!firebaseChatId) {
          throw new Error('Nie udało się utworzyć czatu');
        }

        // Zarejestruj czat w API
        try {
          await createChatInApi({
            announcementId,
            data: {
              firebaseChatId,
              meetingAnnouncementApplicationId: application.id,
            },
          });
        } catch (apiError: any) {
          // Ignoruj błąd jeśli czat już istnieje w API
          console.log('Chat may already exist in API:', apiError.message);
        }

        // Poczekaj na odświeżenie listy czatów z API przed nawigacją
        await queryClient.refetchQueries({
          queryKey: meetingChatKeys.mine(),
        });

        // Nawiguj do czatu (pełny widok split view)
        navigation.navigate('TabNavigator', {
          screen: 'Chat',
          params: {
            initialChatId: firebaseChatId,
            initialChatName: participantData.odataName,
            initialChatImageUri: participantData.odataAvatar || '',
            initialChatMeetingInfo: meetingInfo,
          },
        });

      } catch (error: any) {
        console.error('Error starting chat:', error);
        Alert.alert(t('common.error'), error.message || 'Nie udało się rozpocząć czatu');
      } finally {
        setStartingChatForId(null);
      }
    },
    [myProfile, confirmApplication, createChatInApi, announcement, announcementId, navigation, t, existingChats],
  );

  // Format date for header
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const day = date.getDate();
    const months = [
      'stycznia',
      'lutego',
      'marca',
      'kwietnia',
      'maja',
      'czerwca',
      'lipca',
      'sierpnia',
      'września',
      'października',
      'listopada',
      'grudnia',
    ];
    return `${day} ${months[date.getMonth()]}`;
  };

  const formatTime = (timeStr?: string) => {
    if (!timeStr) return '';
    return timeStr.substring(0, 5);
  };

  return (
    <Screen edges={['top']}>
      <YStack flex={1} backgroundColor={bgColor}>
        {/* Header */}
        <ScreenHeader
          title={t('screens.announcementApplications.title')}
          onBackPress={() => navigation.goBack()}
        />

        <ScrollView
          contentContainerStyle={{ padding: 16, paddingBottom: 100 }}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl
              refreshing={isRefetching}
              onRefresh={handleRefresh}
              tintColor={COLORS.PRIMARY}
            />
          }
        >
          {/* Announcement Summary Card */}
          {announcement && (
            <YStack
              backgroundColor={cardBg}
              borderRadius={16}
              padding="$4"
              borderWidth={1}
              borderColor={borderColor}
              marginBottom="$4"
              gap="$2"
            >
              <Text
                fontSize={16}
                fontWeight="600"
                fontFamily="Poppins"
                color={textColor}
              >
                {announcement.additionalInfo ||
                  t('screens.announcementApplications.meeting')}
              </Text>

              <XStack gap="$4" flexWrap="wrap">
                <XStack alignItems="center" gap="$1">
                  <Calendar size={14} color={subtitleColor} />
                  <Text
                    fontSize={12}
                    fontFamily="Poppins"
                    color={subtitleColor}
                  >
                    {formatDate(announcement.meetingDate)},{' '}
                    {formatTime(announcement.meetingTime)}
                  </Text>
                </XStack>
                <XStack alignItems="center" gap="$1">
                  <MapPin size={14} color={subtitleColor} />
                  <Text
                    fontSize={12}
                    fontFamily="Poppins"
                    color={subtitleColor}
                    numberOfLines={1}
                  >
                    {announcement.meetingAddress}
                  </Text>
                </XStack>
              </XStack>

              <XStack alignItems="center" gap="$1">
                <Users size={14} color={subtitleColor} />
                <Text fontSize={12} fontFamily="Poppins" color={subtitleColor}>
                  {announcement.participantCount}{' '}
                  {t('screens.announcementApplications.person')}
                </Text>
              </XStack>
            </YStack>
          )}

          {/* Applications Count Card */}
          <YStack
            backgroundColor={cardBg}
            borderRadius={16}
            padding="$4"
            borderWidth={1}
            borderColor={borderColor}
            marginBottom="$4"
            alignItems="center"
          >
            <Text
              fontSize={32}
              fontWeight="bold"
              fontFamily="Poppins"
              color={textColor}
            >
              {applications.length}
            </Text>
            <Text fontSize={14} fontFamily="Poppins" color={subtitleColor}>
              {t('screens.announcementApplications.applicationsReceived')}
            </Text>
          </YStack>

          {/* Applications List */}
          {isLoading ? (
            <YStack
              flex={1}
              alignItems="center"
              justifyContent="center"
              paddingVertical="$8"
            >
              <ActivityIndicator size="large" color={COLORS.PRIMARY} />
              <Text fontSize={14} color={subtitleColor} marginTop="$3">
                {t('screens.announcementApplications.loading')}
              </Text>
            </YStack>
          ) : isError ? (
            <YStack
              flex={1}
              alignItems="center"
              justifyContent="center"
              paddingVertical="$8"
            >
              <Text fontSize={16} color={textColor}>
                {t('screens.announcementApplications.error')}
              </Text>
              <TouchableOpacity onPress={handleRefresh}>
                <Text fontSize={14} color={COLORS.PRIMARY} marginTop="$2">
                  {t('screens.announcementApplications.retry')}
                </Text>
              </TouchableOpacity>
            </YStack>
          ) : applications.length === 0 ? (
            <YStack
              flex={1}
              alignItems="center"
              justifyContent="center"
              paddingVertical="$8"
            >
              <Text fontSize={16} color={subtitleColor}>
                {t('screens.announcementApplications.noApplications')}
              </Text>
            </YStack>
          ) : (
            <YStack gap="$3">
              {applications.map(application => (
                <ApplicationCard
                  key={application.id}
                  application={application}
                  cardBg={cardBg}
                  textColor={textColor}
                  subtitleColor={subtitleColor}
                  borderColor={borderColor}
                  isNew={isApplicationNew(application.id)}
                  onViewProfile={() =>
                    handleViewProfile(application.applicantId)
                  }
                  onStartChat={(applicantProfile) => handleStartChat(application, applicantProfile)}
                  isStartingChat={startingChatForId === application.id}
                />
              ))}
            </YStack>
          )}
        </ScrollView>
      </YStack>
    </Screen>
  );
};

export default AnnouncementApplicationsScreen;
